import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import { Site } from '~hooks/useSites/models';

import { SiteCardList } from './SiteCardList';

const EXPANDED_WIDTH = 500;
const COLLAPSED_WIDTH = 50;
const TRANSITION_DURATION = '0.3s';

interface SitesSidebarProps {
  sites: Site[];
  selectedSiteId: string | null;
  onSiteSelect: (siteId: string) => void;
  onSiteEdit: (siteId: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isLoading?: boolean;
}

export function SitesSidebar({
  sites,
  selectedSiteId,
  onSiteSelect,
  onSiteEdit,
  isCollapsed,
  onToggleCollapse,
  isLoading,
}: SitesSidebarProps) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        minWidth: isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        height: '100%',
        borderRight: `1px solid ${theme.brandV2.colors.treadGray7}`,
        transition: `width ${TRANSITION_DURATION} ease, min-width ${TRANSITION_DURATION} ease`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: theme.semantic.surfaceRaised,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          p: 1,
          borderBottom: `1px solid ${theme.brandV2.colors.treadGray7}`,
          minHeight: 48,
        }}
      >
        {!isCollapsed && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '14px',
                color: theme.brandV2.colors.treadBlack,
              }}
            >
              {t('administration.site.title')}
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                color: theme.brandV2.colors.treadGray4,
              }}
            >
              ({sites.length})
            </Typography>
          </Box>
        )}
        <IconButton
          size="small"
          onClick={onToggleCollapse}
          sx={{
            '&:hover': { backgroundColor: theme.brandV2.colors.treadGray8 },
          }}
        >
          {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Box>
      {!isCollapsed && (
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <SiteCardList
            sites={sites}
            selectedSiteId={selectedSiteId}
            onSiteSelect={onSiteSelect}
            onSiteEdit={onSiteEdit}
            isLoading={isLoading}
          />
        </Box>
      )}
    </Box>
  );
}
